import { useState, useEffect, useCallback, useRef } from "react";
import { fetchAllCarts } from "../../service/api/orders";
import {fetchCustomerMessagingTemplates, sendCustomerWhatsappMessage,} from "../../service/api/adminApi";
import "../../css/CartManagement.css";
import PlaceOrderModal from "../../components/modal/PlaceOrderModal";
import { normalizeCartSelection, toggleCartSelection, isAllVisibleCartsSelected } from "../../utils/utils";

const POLL_INTERVAL_MS = 10000;

function formatTimestamp(dateStr) {
    if (!dateStr) return { relative: "—", exact: "" };
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    let relative;
    if (diffMin < 1) relative = "Just now";
    else if (diffMin < 60) relative = `${diffMin}m ago`;
    else if (diffHr < 24) relative = `${diffHr}h ago`;
    else if (diffDay === 1) relative = "Yesterday";
    else if (diffDay < 7) relative = `${diffDay}d ago`;
    else relative = date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });

    const exact = date.toLocaleString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });

    return { relative, exact };
}

function Timestamp({ value }) {
    const { relative, exact } = formatTimestamp(value);
    return (
        <div className="cm-timestamp">
            <span className="cm-timestamp-relative">{relative}</span>
            <span className="cm-timestamp-exact">{exact}</span>
        </div>
    );
}

function SkeletonRow() {
    return (
        <tr>
            {[180, 160, 50, 70, 90, 130, 90].map((w, i) => (
                <td key={i} className="cm-skeleton-td">
                    <div className="cm-skeleton" style={{ height: 14, width: w }} />
                </td>
            ))}
        </tr>
    );
}

function AddonsList({ addons }) {
    if (!addons || addons.length === 0) return <span className="cm-no-addons">No addons</span>;
    return (
        <>
            {addons.map((a, i) => (
                <span key={i} className="cm-addon-line">
                    + {a.addon_name} <span className="cm-addon-price">₹{a.price}</span>
                </span>
            ))}
        </>
    );
}

function RefreshIcon({ size = 15, spinning = false, ...props }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={spinning ? "cm-spin" : ""}
            {...props}
        >
            <path
                d="M4 12a8 8 0 0 1 13.66-5.66M20 12a8 8 0 0 1-13.66 5.66"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
            <path d="M17 3v4.5H12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 21v-4.5h4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function CloseIcon({ size = 14, ...props }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
    );
}

function CartDetailModal({ cart, onClose }) {
    if (!cart) return null;
    return (
        <div className="cm-overlay" onClick={onClose}>
            <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cm-modal-header">
                    <div>
                        <div className="cm-modal-title-row">
                            <span className="cm-modal-title">Cart #{cart.id}</span>
                        </div>
                        <div className="cm-modal-subtitle">{cart.user?.name} → {cart.restaurant?.name}</div>
                    </div>
                    <button className="cm-close-btn" onClick={onClose}><CloseIcon /></button>
                </div>

                <div className="cm-modal-meta">
                    <div className="cm-meta-item">
                        <span className="cm-meta-label">Customer</span>
                        <span className="cm-meta-value">{cart.user?.name}</span>
                    </div>
                    <div className="cm-meta-item">
                        <span className="cm-meta-label">Phone</span>
                        <span className="cm-meta-value">{cart.user?.phone ?? "—"}</span>
                    </div>
                    <div className="cm-meta-item">
                        <span className="cm-meta-label">Restaurant</span>
                        <span className="cm-meta-value">{cart.restaurant?.name}</span>
                    </div>
                    <div className="cm-meta-item">
                        <span className="cm-meta-label">Last Updated</span>
                        <span className="cm-meta-value">{formatTimestamp(cart.updated_at).relative}</span>
                    </div>
                </div>

                <div className="cm-modal-body">
                    <table className="cm-line-table">
                        <thead>
                        <tr>
                            <th className="cm-line-th">Item</th>
                            <th className="cm-line-th">Qty</th>
                            <th className="cm-line-th">Price</th>
                            <th className="cm-line-th">Addons</th>
                            <th className="cm-line-th">Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        {cart.items?.map((item) => (
                            <tr key={item.id}>
                                <td className="cm-line-td">
                                    <div className="cm-item-row">
                                        {item.image && <img src={`https://food.totalfresh.co/${item.image}`} alt={item.name} className="cm-item-thumb" />}
                                        <div>
                                            <div className="cm-item-name">{item.name}</div>
                                            {item.special_instructions && (
                                                <div className="cm-item-note">"{item.special_instructions}"</div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="cm-line-td">{item.quantity}</td>
                                <td className="cm-line-td">₹{item.price}</td>
                                <td className="cm-line-td"><AddonsList addons={item.selected_addons} /></td>
                                <td className="cm-line-td"><strong>₹{item.line_total}</strong></td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                <div className="cm-modal-footer">
                    <span className="cm-footer-count">{cart.total_quantity} items in cart</span>
                    <span className="cm-footer-total">Subtotal: <span>₹{cart.subtotal}</span></span>
                </div>
            </div>
        </div>
    );
}

function CustomerTargetSummary({ targetType, filterLabel, search, customerCount }) {
    return (
        <div className="customers-target-summary">
            <div className="customers-target-row">
                <span>Target Type</span>
                <strong>{targetType}</strong>
            </div>
            <div className="customers-target-row">
                <span>Filter</span>
                <strong>{filterLabel}</strong>
            </div>
            <div className="customers-target-row">
                <span>Search</span>
                <strong>{search || "-"}</strong>
            </div>
            <div className="customers-target-row">
                <span>Carts</span>
                <strong>{customerCount}</strong>
            </div>
        </div>
    );
}

const normalizeTemplates = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.templates)) return response.templates;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.templates)) return response.data.templates;
    return [];
};

const normalizePlaceholderList = (placeholders) => {
    if (Array.isArray(placeholders)) {
        return placeholders.map((placeholder, index) => {
            if (typeof placeholder === "string" || typeof placeholder === "number") {
                return String(placeholder);
            }
            return String(
                placeholder?.key ||
                placeholder?.name ||
                placeholder?.variable ||
                placeholder?.placeholder ||
                index + 1
            );
        });
    }
    if (placeholders && typeof placeholders === "object") {
        return Object.keys(placeholders);
    }
    return [];
};

const getTemplatePlaceholders = (template) => {
    const suppliedPlaceholders = normalizePlaceholderList(template?.placeholders);
    if (suppliedPlaceholders.length > 0) return suppliedPlaceholders;
    const body = template?.body || template?.content || template?.message || "";
    const matches = [...String(body).matchAll(/\{\s*([^}]+?)\s*\}/g)];
    return [...new Set(matches.map((match) => match[1].trim()))];
};

function CustomerWhatsappDrawer({
                                    open,
                                    onClose,
                                    onSend,
                                    selectedTemplate,
                                    setSelectedTemplate,
                                    targetType,
                                    filterLabel,
                                    search,
                                    customerCount,
                                }) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return undefined;

        let active = true;
        setTemplates([]);
        setSelectedTemplate("");
        setError("");
        setLoadingTemplates(true);

        fetchCustomerMessagingTemplates()
            .then((response) => {
                if (active) setTemplates(normalizeTemplates(response));
            })
            .catch((requestError) => {
                if (active) {
                    setError(
                        requestError?.response?.data?.message ||
                        "Failed to load WhatsApp templates."
                    );
                }
            })
            .finally(() => {
                if (active) setLoadingTemplates(false);
            });

        return () => {
            active = false;
        };
    }, [open, setSelectedTemplate]);

    if (!open) return null;

    const template = templates.find(
        (item) => String(item.id ?? item.template_id ?? item.name) === String(selectedTemplate)
    );
    const templatePlaceholders = getTemplatePlaceholders(template);
    const autoPlaceholders = normalizePlaceholderList(template?.auto_placeholders);
    const manualPlaceholders = normalizePlaceholderList(template?.manual_placeholders);
    const hasUnsupportedPlaceholders = manualPlaceholders.length > 0;
    const isBulkSafe = template?.is_bulk_safe !== false;
    const manualPlaceholderWarning =
        "This template contains unsupported/manual variables and cannot be sent.";
    const templatePreview =
        template?.body || template?.content || template?.message || "Select a template to preview it.";

    const handleTemplateChange = (event) => {
        const value = event.target.value;
        setSelectedTemplate(value);
        setError("");
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!selectedTemplate) {
            setError("Please select a WhatsApp template.");
            return;
        }
        if (customerCount <= 0) {
            setError("Select at least one customer.");
            return;
        }
        if (!isBulkSafe || hasUnsupportedPlaceholders) {
            setError(
                hasUnsupportedPlaceholders
                    ? manualPlaceholderWarning
                    : template?.warning || "This template is not valid for customer bulk messaging."
            );
            return;
        }

        setSending(true);
        setError("");
        try {
            await onSend({
                template_id: template?.id ?? template?.template_id ?? selectedTemplate,
            });
        } catch (requestError) {
            setError(
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Failed to send WhatsApp messages."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="customers-drawer-overlay" role="presentation" onMouseDown={onClose}>
            <aside
                className="customers-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="customers-whatsapp-drawer-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="customers-drawer-head">
                    <div>
                        <div className="customers-modal-kicker">Bulk Messaging</div>
                        <h3 className="customers-drawer-title" id="customers-whatsapp-drawer-title">
                            Send WhatsApp Message
                        </h3>
                        <p className="customers-drawer-subtitle">
                            Choose a customer template. Personal details are filled automatically.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="customers-drawer-close"
                        onClick={onClose}
                        disabled={sending}
                        aria-label="Close WhatsApp drawer"
                    >
                        ×
                    </button>
                </div>

                <form className="customers-drawer-form" onSubmit={submit}>
                    <div className="customers-drawer-body">
                        <CustomerTargetSummary
                            targetType={targetType}
                            filterLabel={filterLabel}
                            search={search}
                            customerCount={customerCount}
                        />

                        <div className="customers-message-field">
                            <label htmlFor="customers-whatsapp-template">Template</label>
                            <select
                                id="customers-whatsapp-template"
                                value={selectedTemplate}
                                onChange={handleTemplateChange}
                                disabled={loadingTemplates || sending}
                                required
                            >
                                <option value="">
                                    {loadingTemplates ? "Loading templates..." : "Select a template"}
                                </option>
                                {templates.map((item) => {
                                    const value = item.id ?? item.template_id ?? item.name;
                                    const itemName =
                                        item.name || item.title || item.template_name || `Template ${value}`;
                                    const itemIsBulkSafe = item.is_bulk_safe !== false;
                                    return (
                                        <option
                                            key={value}
                                            value={value}
                                            disabled={!itemIsBulkSafe}
                                        >
                                            {itemIsBulkSafe
                                                ? itemName
                                                : `${itemName} - Not valid for customer bulk`}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {!loadingTemplates && templates.length === 0 && !error && (
                            <div className="customers-drawer-empty">No WhatsApp templates available.</div>
                        )}

                        <div className="customers-template-preview">
                            <span>Template Preview</span>
                            <p>{templatePreview}</p>
                        </div>

                        {autoPlaceholders.length > 0 && (
                            <div className="customers-auto-placeholders">
                                <span className="customers-auto-placeholders-title">
                                    Auto-filled for each customer
                                </span>
                                <div className="customers-placeholder-badges">
                                    {autoPlaceholders.map((placeholder) => (
                                        <span className="customers-placeholder-badge" key={placeholder}>
                                            {placeholder}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {template && templatePlaceholders.length === 0 && (
                            <div className="customers-auto-placeholders customers-auto-placeholders-empty">
                                This template does not require customer variables.
                            </div>
                        )}

                        {template && (!isBulkSafe || hasUnsupportedPlaceholders) && (
                            <div className="customers-drawer-error">
                                {hasUnsupportedPlaceholders
                                    ? manualPlaceholderWarning
                                    : template?.warning ||
                                    "This template is not valid for customer bulk messaging."}
                                {hasUnsupportedPlaceholders && (
                                    <div className="customers-placeholder-badges">
                                        {manualPlaceholders.map((placeholder) => (
                                            <span
                                                className="customers-placeholder-badge unsupported"
                                                key={placeholder}
                                            >
                                                {placeholder}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {error && <div className="customers-drawer-error">{error}</div>}
                    </div>

                    <div className="customers-drawer-footer">
                        <button
                            type="button"
                            className="customers-bulk-btn clear"
                            onClick={onClose}
                            disabled={sending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="customers-bulk-btn whatsapp"
                            disabled={
                                sending ||
                                loadingTemplates ||
                                customerCount <= 0 ||
                                !selectedTemplate ||
                                !isBulkSafe ||
                                hasUnsupportedPlaceholders
                            }
                        >
                            {sending ? "Sending..." : "Send WhatsApp"}
                        </button>
                    </div>
                </form>
            </aside>
        </div>
    );
}
export default function Carts() {
    const [carts, setCarts] = useState([]);
    const [loading, setLoading] = useState(true);     // initial / page-change load → shows skeleton
    const [refreshing, setRefreshing] = useState(false); // background poll / manual reload → no skeleton
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [meta, setMeta] = useState({ total: 0, last_page: 1 });
    const [selected, setSelected] = useState(null);
    const [orderTarget, setOrderTarget] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [toast, setToast] = useState(null);
    const [selectedCarts, setSelectedCarts] = useState([]);
    const [selectAllMatching, setSelectAllMatching] = useState(false);

    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [, forceTick] = useState(0); // re-render periodically so "updated Xs ago" stays fresh

    const debounceRef = useRef(null);
    const pollRef = useRef(null);
    const inFlightRef = useRef(false);

    const load = useCallback(async (pg, pp, { silent = false } = {}) => {
        // avoid overlapping requests (e.g. manual reload while a poll is in flight)
        if (inFlightRef.current) return;
        inFlightRef.current = true;

        if (silent) setRefreshing(true);
        else setLoading(true);

        try {
            const res = await fetchAllCarts({ page: pg, per_page: pp });
            setCarts(res.data ?? []);
            setMeta({
                total: res.pagination?.total ?? 0,
                last_page: res.pagination?.last_page ?? 1,
            });
            setLastUpdated(new Date());
        } catch (e) {
            console.error(e);
        } finally {
            inFlightRef.current = false;
            if (silent) setRefreshing(false);
            else setLoading(false);
        }
    }, []);

    // Initial load + reload whenever page/perPage changes (shows skeleton),
    // and (re)start a 10s auto-poll on the current page (silent, no skeleton).
    useEffect(() => {
        clearTimeout(debounceRef.current);
        clearInterval(pollRef.current);

        debounceRef.current = setTimeout(() => load(page, perPage), 150);
        pollRef.current = setInterval(() => load(page, perPage, { silent: true }), POLL_INTERVAL_MS);

        return () => {
            clearTimeout(debounceRef.current);
            clearInterval(pollRef.current);
        };
    }, [page, perPage, load]);

    // Keep the "updated Xs ago" label ticking without refetching.
    useEffect(() => {
        const t = setInterval(() => forceTick((n) => n + 1), 5000);
        return () => clearInterval(t);
    }, []);

    const handleManualReload = () => {
        clearInterval(pollRef.current); // reset the poll timer so it doesn't fire right after a manual one
        load(page, perPage, { silent: true }).then(() => {
            pollRef.current = setInterval(() => load(page, perPage, { silent: true }), POLL_INTERVAL_MS);
        });
    };

    const buildPages = () => {
        const pages = [];
        const total = meta.last_page;
        if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); return pages; }
        pages.push(1);
        if (page > 3) pages.push("…");
        for (let i = Math.max(2, page - 1); i <= Math.min(total - 1, page + 1); i++) pages.push(i);
        if (page < total - 2) pages.push("…");
        pages.push(total);
        return pages;
    };


    const showToast = (type = "success", msg) => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleCartSelectionToggle = useCallback((cartId) => {
        setSelectedCarts((current) => {
            const next = toggleCartSelection(current, cartId);
            return normalizeCartSelection(next);
        });
        setSelectAllMatching(false);
    }, []);

    const clearBulkSelection = useCallback(() => {
        setSelectedCarts([]);
        setSelectAllMatching(false);
    }, []);

const buildTargetPayload = useCallback(() => {
    if (selectAllMatching) {
        return {
            target_type: "active_carts",
        };
    }

    const userIds = carts
        .filter((cart) => selectedCarts.includes(cart.id))
        .map((cart) => cart.user?.id)
        .filter(Boolean);

    return {
        target_type: "selected",
        user_ids: [...new Set(userIds)],
    };
}, [selectAllMatching, selectedCarts, carts]);

   const handleSendWhatsapp = async (form) => {
    try {
        const response = await sendCustomerWhatsappMessage({
            ...buildTargetPayload(),
            ...form,
        });

        setShowWhatsappModal(false);
        clearBulkSelection();
        setSelectedTemplate("");

        const summary = response?.summary || {};
        const sent = summary.sent_count || 0;
        const failed = summary.failed_count || 0;
        const skipped = summary.skipped_count || 0;

        showToast(
            "success",
            `WhatsApp sent successfully. Sent: ${sent}, Failed: ${failed}, Skipped: ${skipped}`
        );
    } catch (error) {
        showToast(
            "error",
            error?.response?.data?.message ||
            error?.message ||
            "Please try again."
        );
        throw error;
    }
};

    const visibleCartIds = carts.map((cart) => cart.id);
    const targetCount = selectAllMatching
        ? meta.total
        : selectedCarts.length;
    const isHeaderCheckboxChecked = !loading && carts.length > 0 && isAllVisibleCartsSelected(visibleCartIds, selectedCarts);

    const targetTypeLabel = selectAllMatching
        ? "All Active Carts"
        : "Selected Active Carts";
    const totalValue = carts.reduce((sum, c) => sum + parseFloat(c.subtotal || 0), 0);
    const lastUpdatedLabel = lastUpdated ? formatTimestamp(lastUpdated.toISOString()).relative : null;

    return (
        <div style={{ margin: 25 }}>
            <div className="cm-page-header">
                <div>
                    <h1 className="cm-page-title">Active Carts</h1>
                    <div className="cm-page-subtitle">Live carts currently in customers' hands</div>
                </div>
                <div className="cm-live-indicator">
                    <span className="cm-live-dot" />
                    <span>
                        Auto-refreshing every {POLL_INTERVAL_MS / 1000}s
                        {lastUpdatedLabel && ` · Updated ${lastUpdatedLabel}`}
                    </span>
                </div>
            </div>

            <div className="cm-card">

                <div className="cm-toolbar">
                    <div className="cm-toolbar-stats">
                        <div className="cm-stat">
                            <span className="cm-stat-value">{loading ? "—" : meta.total}</span>
                            <span className="cm-stat-label">Total Carts</span>
                        </div>
                        <div className="cm-stat">
                            <span className="cm-stat-value">{loading ? "—" : `₹${totalValue.toFixed(0)}`}</span>
                            <span className="cm-stat-label">This Page Value</span>
                        </div>
                    </div>

                    <div className="cm-toolbar-actions">
                        <button
                            className="cm-reload-btn"
                            onClick={() => {
                                setSelectAllMatching((current) => !current);
                                setSelectedCarts([]);
                            }}
                            disabled={loading || meta.total === 0}
                            title={selectAllMatching ? "Clear all active carts selection" : "Select all active carts"}
                        >
                            {selectAllMatching ? "Clear Active Carts" : "Select All Active Carts"}
                        </button>

                        <button
                            className="cm-reload-btn cm-view-btn--primary"
                            onClick={() => setShowWhatsappModal(true)}
                            disabled={loading || targetCount <= 0}
                            title="Send WhatsApp to the selected carts"
                        >
                            WhatsApp
                        </button>

                        <button
                            className="cm-reload-btn"
                            onClick={handleManualReload}
                            disabled={refreshing || loading}
                            title="Reload now"
                        >
                            <RefreshIcon spinning={refreshing} />
                            {refreshing ? "Refreshing…" : "Reload"}
                        </button>

                        <select
                            className="cm-select"
                            value={perPage}
                            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                        >
                            {[15, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                        </select>
                    </div>
                </div>
                <div>{selectAllMatching && (
                    <div className="customers-bulk-warning">
                        Messages will be sent to all active carts.
                    </div>
                )}</div>
                {/* Desktop table */}
                <div className="cm-table-wrap">
                    <table className="cm-table">
                        <thead className="cm-thead">
                        <tr>
                            <th className="cm-th">
                                <input
                                    type="checkbox"
                                    checked={isHeaderCheckboxChecked}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCarts((current) => normalizeCartSelection([...current, ...visibleCartIds]));
                                        } else {
                                            setSelectedCarts((current) => current.filter((id) => !visibleCartIds.includes(id)));
                                        }

                                        setSelectAllMatching(false);
                                    }}
                                />
                            </th>

                            <th className="cm-th">Customer</th>
                            <th className="cm-th">Restaurant</th>
                            <th className="cm-th">Items</th>
                            <th className="cm-th">Qty</th>
                            <th className="cm-th">Subtotal</th>
                            <th className="cm-th">Last Updated</th>
                            <th className="cm-th"></th>
                        </tr>
                        </thead>
                        <tbody className="cm-tbody">
                        {loading
                            ? Array.from({ length: Math.min(perPage, 10) }).map((_, i) => <SkeletonRow key={i} />)
                            : carts.length === 0
                                ? (
                                    <tr>
                                        <td colSpan={8}>
                                            <div className="cm-empty">
                                                <div className="cm-empty__icon">🛒</div>
                                                <div className="cm-empty__title">No active carts</div>
                                                <div className="cm-empty__sub">Carts will appear here once customers start adding items</div>
                                            </div>
                                        </td>
                                    </tr>
                                )
                                : carts.map((cart) => (
                                    <tr key={cart.id}>
                                        <td className="cm-td">
                                            <input
                                                type="checkbox"
                                                checked={selectAllMatching ? false : selectedCarts.includes(cart.id)}
                                                onChange={() => handleCartSelectionToggle(cart.id)}
                                                className="cm-row-checkbox"
                                            />
                                        </td>
                                        <td className="cm-td">
                                            <div className="cm-customer">

                                                <span className="cm-customer-name">{cart.user?.name}</span>
                                                <span className="cm-customer-phone">{cart.user?.phone ?? "—"}</span>

                                            </div>
                                        </td>
                                        <td className="cm-td">
                                            <div className="cm-restaurant">
                                                {cart.restaurant?.image && (
                                                    <img src={`https://food.totalfresh.co/${cart.restaurant.image}`}
                                                         alt="" className="cm-restaurant-avatar"/>
                                                )}
                                                <span className="cm-restaurant-name">{cart.restaurant?.name}</span>
                                            </div>
                                        </td>
                                        <td className="cm-td"><span className="cm-pill">{cart.total_items}</span></td>
                                        <td className="cm-td"><span className="cm-pill">{cart.total_quantity}</span>
                                        </td>
                                        <td className="cm-td"><span className="cm-subtotal">₹{cart.subtotal}</span></td>
                                        <td className="cm-td"><Timestamp value={cart.updated_at}/></td>
                                        <td className="cm-td">
                                            <button className="cm-view-btn" onClick={() => setSelected(cart)}>View
                                            </button>
                                            <button className="cm-view-btn cm-view-btn--primary"
                                                    onClick={() => setOrderTarget(cart)}>
                                                Place Order
                                            </button>
                                        </td>
                                    </tr>
                                ))
                        }
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="cm-card-list">
                    {!loading && carts.length > 0 && (
                        <div className="cm-mobile-select-bar">
                            <label className="cm-mobile-select-label">
                                <input
                                    type="checkbox"
                                    checked={selectAllMatching}
                                    onChange={() => {
                                        setSelectAllMatching((current) => !current);
                                        setSelectedCarts([]);
                                    }}
                                />
                                <span>Select All Active Carts</span>
                            </label>
                        </div>
                    )}
                    {loading
                        ? Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="cm-card-row">
                                <div className="cm-skeleton" style={{ height: 16, width: "60%", marginBottom: 8 }} />
                                <div className="cm-skeleton" style={{ height: 12, width: "40%" }} />
                            </div>
                        ))
                        : carts.length === 0
                            ? (
                                <div className="cm-empty">
                                    <div className="cm-empty__icon">🛒</div>
                                    <div className="cm-empty__title">No active carts</div>
                                    <div className="cm-empty__sub">Carts will appear here once customers start adding items</div>
                                </div>
                            )
                            : carts.map((cart) => (
                                <div key={cart.id} className="cm-card-row">
                                    <div className="cm-card-row-top">
                                        <label className="cm-mobile-card-select">
                                            <input
                                                type="checkbox"
                                                checked={selectAllMatching ? false : selectedCarts.includes(cart.id)}
                                                onChange={() => handleCartSelectionToggle(cart.id)}
                                            />
                                        </label>
                                        <div className="cm-customer">
                                            <span className="cm-customer-name">{cart.user?.name}</span>
                                            <span className="cm-customer-phone">{cart.restaurant?.name}</span>
                                        </div>
                                        <span className="cm-subtotal">₹{cart.subtotal}</span>
                                    </div>
                                    <div className="cm-card-meta">
                                        <div className="cm-meta-item">
                                            <span className="cm-meta-label">Items</span>
                                            <span className="cm-meta-value">{cart.total_items} ({cart.total_quantity} qty)</span>
                                        </div>
                                        <div className="cm-meta-item">
                                            <span className="cm-meta-label">Updated</span>
                                            <span className="cm-meta-value">{formatTimestamp(cart.updated_at).relative}</span>
                                        </div>
                                    </div>
                                    <div className="cm-card-footer">
                                        <span className="cm-customer-phone">{cart.user?.phone ?? "—"}</span>
                                        <button className="cm-view-btn" onClick={() => setSelected(cart)}>View</button>
                                        <button className="cm-view-btn cm-view-btn--primary"
                                                onClick={() => setOrderTarget(cart)}>
                                            Place Order
                                        </button>
                                    </div>
                                </div>
                            ))
                    }
                </div>

                {!loading && carts.length > 0 && (
                    <div className="cm-pagination">
                        <span className="cm-pagination-info">Page {page} of {meta.last_page} · {meta.total.toLocaleString()} carts</span>
                        <div className="cm-pagination-pages">
                            <button className="cm-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
                            {buildPages().map((p, i) =>
                                p === "…"
                                    ? <span key={`e${i}`} className="cm-page-ellipsis">…</span>
                                    : <button key={p} className={`cm-page-btn${page === p ? " cm-page-btn--active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                            )}
                            <button className="cm-page-btn" disabled={page === meta.last_page} onClick={() => setPage((p) => p + 1)}>›</button>
                        </div>
                    </div>
                )}
            </div>

            <CartDetailModal cart={selected} onClose={() => setSelected(null)} />

            <CustomerWhatsappDrawer
                open={showWhatsappModal}
                onClose={() => setShowWhatsappModal(false)}
                onSend={handleSendWhatsapp}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                targetType={targetTypeLabel}
                filterLabel="Active carts"
                search=""
                customerCount={targetCount}
            />

            {orderTarget && (
                <PlaceOrderModal
                    cart={orderTarget}
                    onClose={() => setOrderTarget(null)}
                    onPlaced={() => load(page, perPage, { silent: true })}
                    showToast={showToast}
                />
            )}

            {toast && (
                <div className={`wt-toast wt-toast--${toast.type}`}>{toast.msg}</div>
            )}
        </div>
    );
}